import React, { useEffect, useRef, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import { auth, googleProvider, db } from '../firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signInWithPopup, updateProfile } from 'firebase/auth';
import { doc, setDoc, increment } from 'firebase/firestore';

/* ─────────────────────────────────────────────────────────────────────────────
   DOMINATION PALETTE
   Primary  : #8B00FF  — Electric Violet
   Accent   : #FF006E  — Neon Magenta / Crimson
   Gold     : #FFD700  — Royal Gold
   Glow 1   : #BF00FF  — Purple Plasma
   Glow 2   : #FF2D78  — Hot Pink
───────────────────────────────────────────────────────────────────────────── */

/* ─────────────────────────────────────────────────────────────────────────────
   HACK SEQUENCE — Extended terminal logs for breach animation
───────────────────────────────────────────────────────────────────────────── */
const HACK_LINES = [
  { text: '> INITIATING DOMINATION PROTOCOL v7.4...',       color: '#BF00FF',              delay: 0 },
  { text: '> SCANNING TARGET SYSTEM ARCHITECTURE...',       color: '#BF00FF',              delay: 350 },
  { text: '  ▸ OS: NEURAL_OS // KERNEL 5.19.0-JARVIS',     color: 'rgba(191,0,255,0.6)', delay: 700 },
  { text: '  ▸ ARCH: X86_64 | RAM: 32GB | CORES: 16',     color: 'rgba(191,0,255,0.5)', delay: 1000 },
  { text: '  ▸ FIREWALL NODES DETECTED: 6',                color: '#FF006E',              delay: 1300 },
  { text: '> BYPASSING FIREWALL LAYER 1/4 [AES-256]...',   color: '#BF00FF',              delay: 1700, bar: true, barLabel: 'ENCRYPTION LAYER', barDur: 600 },
  { text: '> BYPASSING FIREWALL LAYER 2/4 [IDS/IPS]...',   color: '#BF00FF',              delay: 2500, bar: true, barLabel: 'SECURITY LAYER',    barDur: 700 },
  { text: '> BYPASSING FIREWALL LAYER 3/4 [DATABASE]...',  color: '#BF00FF',              delay: 3400, bar: true, barLabel: 'DATABASE LAYER',    barDur: 500 },
  { text: '> BYPASSING FIREWALL LAYER 4/4 [PROTOCOL]...',  color: '#BF00FF',              delay: 4100, bar: true, barLabel: 'PROTOCOL LAYER',    barDur: 800 },
  { text: '> ALL FIREWALLS NEUTRALIZED ✓',                  color: '#FF006E',              delay: 5100 },
  { text: '> DECRYPTING NEURAL VAULT MATRIX...',            color: '#BF00FF',              delay: 5500, bar: true, barLabel: 'VAULT DECRYPT',     barDur: 900 },
  { text: '> VERIFYING IDENTITY BIOMETRIC HASH...',         color: 'rgba(191,0,255,0.7)', delay: 6600 },
  { text: '  ▸ SHA-512: 2A4F9B1E77CD...MATCH ✓',          color: '#FF006E',              delay: 7200 },
  { text: '> INJECTING ROOT PRIVILEGES...',                 color: '#BF00FF',              delay: 7700, bar: true, barLabel: 'ROOT ACCESS',       barDur: 700 },
  { text: '> MOUNTING /sys/financial_vault...',             color: 'rgba(191,0,255,0.6)', delay: 8300 },
  { text: '> SYNCHRONIZING INVESTMENT LEDGER...',           color: 'rgba(191,0,255,0.5)', delay: 8600 },
  { text: '> FIREBASE CONNECTED...',                        color: '#FF006E',              delay: 8900 },
  { text: '> SYSTEM COMPROMISED ✓',                         color: '#FF006E',              delay: 9200 },
  { text: '> ESTABLISHING SECURE SESSION...',               color: '#BF00FF',              delay: 9500 },
  { text: '> WELCOME BACK, UDAY_KANCHI',                    color: '#FFD700',              delay: 9900, final: true },
];

/* ─────────────────────────────────────────────────────────────────────────────
   PROGRESS BAR — Animated terminal-style loader bar
───────────────────────────────────────────────────────────────────────────── */
const ProgressBar = ({ label, duration }) => {
  const [pct, setPct] = useState(0);

  useEffect(() => {
    const steps = 40;
    const stepSize = 100 / steps;
    const interval = duration / steps;
    let curr = 0;
    const t = setInterval(() => {
      curr += stepSize;
      setPct(Math.min(Math.round(curr), 100));
      if (curr >= 100) clearInterval(t);
    }, interval);
    return () => clearInterval(t);
  }, [duration]);

  return (
    <div style={{ marginBottom: 14, fontFamily: "'Fira Code', monospace", fontSize: 10, color: 'rgba(191,0,255,0.7)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <span style={{ letterSpacing: '0.15em' }}>{label}</span>
        <span style={{ color: pct === 100 ? '#FFD700' : 'rgba(191,0,255,0.8)' }}>
          {pct < 100 ? `${pct}%` : 'DONE ✓'}
        </span>
      </div>
      <div style={{ background: 'rgba(139,0,255,0.10)', height: 3, borderRadius: 2 }}>
        <div style={{
          height: 3, borderRadius: 2,
          background: pct === 100
            ? 'linear-gradient(90deg, #8B00FF, #FF006E)'
            : 'linear-gradient(90deg, #8B00FF, #BF00FF)',
          boxShadow: pct === 100 ? '0 0 12px #FF006E' : '0 0 10px #8B00FF',
          width: `${pct}%`,
          transition: `width ${duration / 40}ms linear`,
        }} />
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   HACK TERMINAL — The cinematic breach sequence overlay
───────────────────────────────────────────────────────────────────────────── */
const HackTerminal = ({ onComplete }) => {
  const [visibleLines, setVisibleLines] = useState([]);
  const [activeBars, setActiveBars] = useState([]);
  const [showGranted, setShowGranted] = useState(false);
  const termRef = useRef(null);

  useEffect(() => {
    const timers = [];
    HACK_LINES.forEach((line, idx) => {
      timers.push(setTimeout(() => {
        setVisibleLines(prev => [...prev, { ...line, idx }]);
        if (line.bar) setActiveBars(prev => [...prev, { idx, label: line.barLabel, dur: line.barDur }]);
        if (line.final) {
          setTimeout(() => {
            setShowGranted(true);
            setTimeout(onComplete, 2200);
          }, 800);
        }
        if (termRef.current) {
          termRef.current.scrollTo({ top: termRef.current.scrollHeight, behavior: 'smooth' });
        }
      }, line.delay));
    });
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 100,
      background: 'radial-gradient(ellipse at center, #0a0010 0%, #000 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '40px 24px',
    }}>
      {/* Scanline overlay */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.22) 2px, rgba(0,0,0,0.22) 4px)',
      }} />

      {/* Ambient purple glow */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
        background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(139,0,255,0.08) 0%, transparent 70%)',
      }} />

      <div style={{ width: '100%', maxWidth: 480, position: 'relative', zIndex: 1 }}>
        {/* Terminal Title Bar */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          marginBottom: 22, paddingBottom: 14,
          borderBottom: '1px solid rgba(139,0,255,0.2)',
          background: 'rgba(139,0,255,0.04)',
          padding: '10px 16px', borderRadius: '8px 8px 0 0',
        }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f57', boxShadow: '0 0 6px #ff5f57' }} />
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ffbd2e', boxShadow: '0 0 6px #ffbd2e' }} />
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#BF00FF', boxShadow: '0 0 8px #BF00FF' }} />
          <span style={{
            fontFamily: "'Fira Code', monospace", fontSize: 10,
            color: 'rgba(191,0,255,0.45)', letterSpacing: '0.4em', marginLeft: 12,
          }}>
            U-JARVIS // DOMINATION_BREACH_v7.4
          </span>
        </div>

        {/* Terminal Log Output */}
        <div ref={termRef} style={{
          maxHeight: 280, overflowY: 'auto', marginBottom: 20,
          scrollbarWidth: 'none', padding: '0 4px',
        }}>
          {visibleLines.map(line => (
            <div key={line.idx} style={{
              color: line.color, fontFamily: "'Fira Code', monospace",
              fontSize: 11.5, lineHeight: 2.1, letterSpacing: '0.06em',
              animation: 'termFadeIn 0.25s ease',
            }}>
              {line.text}
            </div>
          ))}
        </div>

        {/* Progress Bars */}
        <div style={{ marginBottom: 16, padding: '0 4px' }}>
          {activeBars.map(b => <ProgressBar key={b.idx} label={b.label} duration={b.dur} />)}
        </div>

        {/* ACCESS GRANTED Banner */}
        {showGranted && (
          <div style={{
            textAlign: 'center', paddingTop: 22,
            borderTop: '1px solid rgba(255,0,110,0.25)', marginTop: 16,
          }}>
            <div style={{
              color: '#FFD700', fontSize: 20, fontWeight: 900,
              fontFamily: "'Fira Code', monospace",
              letterSpacing: '0.6em',
              textShadow: '0 0 30px #FFD700, 0 0 60px rgba(255,215,0,0.3)',
              animation: 'pulseGold 0.7s infinite',
            }}>
              ACCESS_GRANTED
            </div>
            <div style={{
              color: 'rgba(191,0,255,0.55)', fontSize: 10,
              fontFamily: "'Fira Code', monospace",
              letterSpacing: '0.25em', marginTop: 10,
            }}>
              UDAY_KANCHI_SESSION // INITIALIZED_SUCCESSFULLY
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes termFadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulseGold  { 0%,100%{opacity:0.8} 50%{opacity:1} }
      `}</style>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   HACKER RINGS — Concentric animated circular loader UI
───────────────────────────────────────────────────────────────────────────── */
const HackerRings = ({ theme, error }) => {
  const GOLD    = '#FFD700';
  const AMBER   = '#FFC107';
  const ORANGE  = '#FF8C00';
  const RED_ERR = '#FF006E';
  const c       = error ? RED_ERR : GOLD;
  const c2      = error ? '#FF3131' : AMBER;
  const c3      = error ? '#cc0044' : ORANGE;

  // Ring configs: [radius, strokeW, dasharray, dur, dir, opacity, label]
  const rings = [
    { r: 220, sw: 0.6, da: '4 18',   dur: 28,  dir: 'CW',  op: 0.18, label: null },
    { r: 200, sw: 1.2, da: '120 80', dur: 12,  dir: 'CW',  op: 0.55, label: 'SCANNING..' },
    { r: 182, sw: 0.5, da: '2 12',   dur: 20,  dir: 'CCW', op: 0.15, label: null },
    { r: 168, sw: 1.8, da: '60 200', dur: 7,   dir: 'CCW', op: 0.70, label: 'BREACH_LOCK' },
    { r: 150, sw: 0.7, da: '8 24',   dur: 15,  dir: 'CW',  op: 0.22, label: null },
    { r: 136, sw: 1.0, da: '40 120', dur: 18,  dir: 'CW',  op: 0.45, label: 'ENCRYPT' },
    { r: 120, sw: 0.4, da: '2 8',    dur: 9,   dir: 'CCW', op: 0.12, label: null },
    { r: 108, sw: 1.5, da: '20 80',  dur: 5,   dir: 'CW',  op: 0.60, label: 'ROOT' },
  ];

  // Tick mark angles for outer reference ring
  const ticks = Array.from({ length: 36 }, (_, i) => i * 10);


  const size = 500;

  return (
    <svg
      width={size} height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{
        position: 'absolute',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 5, pointerEvents: 'none',
        overflow: 'visible',
        animation: 'ringPulse 3s ease-in-out infinite',
      }}
    >
      <defs>
        {/* Gold gradient for dashed arcs */}
        <linearGradient id="rg1" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor={c}  stopOpacity="1" />
          <stop offset="50%"  stopColor={c2} stopOpacity="0.8" />
          <stop offset="100%" stopColor={c3} stopOpacity="0.3" />
        </linearGradient>
        <linearGradient id="rg2" x1="100%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%"   stopColor={c3} stopOpacity="0.5" />
          <stop offset="100%" stopColor={c}  stopOpacity="0.9" />
        </linearGradient>

        {/* Glow filter */}
        <filter id="ringGlow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="2.5" result="blur" />
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>

        {/* Tick mark path template */}
        {ticks.map(deg => (
          <path key={deg}
            id={`tick${deg}`}
            d={`M ${size/2} ${size/2 - 228} L ${size/2} ${size/2 - 234}`}
            stroke={c} strokeWidth="1" opacity="0.3"
            transform={`rotate(${deg} ${size/2} ${size/2})`}
          />
        ))}
      </defs>

      {/* Outer tick ring */}
      <g filter="url(#ringGlow)">
        {ticks.map(deg => (
          <line key={deg}
            x1={size/2} y1={size/2 - 226}
            x2={size/2} y2={size/2 - (deg % 30 === 0 ? 218 : 222)}
            stroke={deg % 30 === 0 ? c : c2}
            strokeWidth={deg % 30 === 0 ? 1.5 : 0.7}
            opacity={deg % 30 === 0 ? 0.7 : 0.25}
            transform={`rotate(${deg} ${size/2} ${size/2})`}
          />
        ))}
      </g>

      {/* Animated rings */}
      {rings.map((ring, i) => (
        <g key={i}
          style={{ animation: `ringRotate${ring.dir} ${ring.dur}s linear infinite` }}
          transform={`rotate(0 ${size/2} ${size/2})`}
          filter={ring.sw > 1 ? 'url(#ringGlow)' : undefined}
        >
          <circle
            cx={size/2} cy={size/2}
            r={ring.r}
            fill="none"
            stroke={i % 2 === 0 ? 'url(#rg1)' : 'url(#rg2)'}
            strokeWidth={ring.sw}
            strokeDasharray={ring.da}
            opacity={ring.op}
          />
          {/* Inline label on some rings */}
          {ring.label && (
            <text
              dy="-6"
              style={{ fontSize: 7, fill: c, opacity: 0.5, letterSpacing: '0.3em', fontFamily: "'Fira Code',monospace" }}
            >
              <textPath
                href={`#labelPath${i}`}
                startOffset="10%"
              >
                {ring.label}
              </textPath>
            </text>
          )}
        </g>
      ))}

      {/* Text paths for labels (static) */}
      {rings.map((ring, i) => ring.label && (
        <defs key={`lp${i}`}>
          <path
            id={`labelPath${i}`}
            d={`M ${size/2 - ring.r},${size/2} a ${ring.r},${ring.r} 0 1,1 ${ring.r * 2},0`}
          />
        </defs>
      ))}

      {/* Center crosshair dot */}
      <circle cx={size/2} cy={size/2} r={4} fill={c} opacity="0.8" filter="url(#ringGlow)" />
      <circle cx={size/2} cy={size/2} r={10} fill="none" stroke={c} strokeWidth="0.8" opacity="0.35" />
      <line x1={size/2 - 16} y1={size/2} x2={size/2 + 16} y2={size/2} stroke={c} strokeWidth="0.6" opacity="0.3" />
      <line x1={size/2} y1={size/2 - 16} x2={size/2} y2={size/2 + 16} stroke={c} strokeWidth="0.6" opacity="0.3" />
    </svg>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   LANDING PAGE — Main component: matrix rain + login card + breach sequence
───────────────────────────────────────────────────────────────────────────── */
const LandingPage = ({ onUnlock }) => {
  const canvasRef = useRef(null);
  const [identity, setIdentity] = useState('');
  const [key, setKey] = useState('');
  const [error, setError] = useState('');
  const [hacking, setHacking] = useState(false);
  const [speed, setSpeed] = useState(38);
  const [focusId, setFocusId] = useState(null);
  const [isSignup, setIsSignup] = useState(false);
  const [displayName, setDisplayName] = useState('');

  /* ── Session persistence check ── */
  // We remove the old sessionStorage check since App.js handles the state
  // and directly sets isUnlocked. If this renders, it means user is null or not unlocked.

  /* ── GOLD Matrix Rain canvas ── */
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const chars = '01アイウエオカキクケコサシスセソタチツテトナニヌネノ$₹#%&*ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    const fh = 14;
    let drops = Array(Math.floor(canvas.width / fh)).fill(1);

    const tick = () => {
      ctx.fillStyle = hacking ? 'rgba(0,0,0,0.04)' : 'rgba(4,2,0,0.09)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = `${fh}px "Fira Code", monospace`;

      for (let j = 0; j < drops.length; j++) {
        const ch = chars[Math.floor(Math.random() * chars.length)];
        const isHead = drops[j] % 5 === 0;
        const isMid  = drops[j] % 3 === 0;

        if (hacking) {
          // Hacking frenzy: blinding white-gold flicker
          ctx.fillStyle = isHead ? '#FFFFFF' : (isMid ? 'rgba(255,215,0,0.95)' : 'rgba(255,180,0,0.6)');
        } else if (isHead) {
          // Brightest lead char — pure gold / amber
          ctx.fillStyle = j % 3 === 0 ? '#FFD700' : (j % 3 === 1 ? '#FFC107' : '#FFAA00');
        } else if (isMid) {
          // Mid trail — warm gold, fading
          ctx.fillStyle = 'rgba(255,200,0,0.38)';
        } else {
          // Dim tail — deep amber
          ctx.fillStyle = 'rgba(180,100,0,0.14)';
        }

        ctx.fillText(ch, j * fh, drops[j] * fh);
        if (drops[j] * fh > canvas.height && Math.random() > 0.975) drops[j] = 0;
        drops[j]++;
      }
    };

    const id = setInterval(tick, speed);
    return () => { clearInterval(id); window.removeEventListener('resize', resize); };
  }, [speed, hacking]);

  /* ── Authentication handler ── */
  const handleSystemBypass = useCallback(async () => {
    try {
      if (!identity || !key) throw new Error("Credentials required");
      
      let finalIdentity = identity.trim();
      if (finalIdentity === 'Uday_Kanchi') {
        finalIdentity = 'uday_kanchi@u-jarvis.com';
      } else if (finalIdentity === 'admin') {
        finalIdentity = 'admin@u-jarvis.com';
      } else if (!finalIdentity.includes('@')) {
        finalIdentity = `${finalIdentity}@u-jarvis.com`;
      }

      if (isSignup) {
        if (!displayName) throw new Error("Display Name required for signup");
        const userCredential = await createUserWithEmailAndPassword(auth, finalIdentity, key);
        await updateProfile(userCredential.user, { displayName });
        await setDoc(doc(db, 'u-jarvis-status', 'analytics'), { totalUsers: increment(1) }, { merge: true });
      } else {
        try {
          await signInWithEmailAndPassword(auth, finalIdentity, key);
        } catch (err) {
          if ((identity.trim() === 'Uday_Kanchi' || identity.trim() === 'admin') && err.code === 'auth/invalid-credential') {
             try {
                const userCredential = await createUserWithEmailAndPassword(auth, finalIdentity, key);
                await updateProfile(userCredential.user, { displayName: identity.trim() === 'admin' ? 'System Admin' : 'Uday Kanchi' });
                await setDoc(doc(db, 'u-jarvis-status', 'analytics'), { totalUsers: increment(1) }, { merge: true });
             } catch (createErr) {
                throw err;
             }
          } else {
             throw err;
          }
        }
      }
      setError('');
      const tl = gsap.timeline();
      tl.to('.lp-card', { duration: 0.6, scale: 0.88, opacity: 0, ease: 'power4.in', onComplete: () => setHacking(true) });
      setSpeed(5);
    } catch (err) {
      console.error(err);
      let errMsg = "IDENTITY VERIFICATION FAILED";
      if (err.code === 'auth/invalid-email') errMsg = "INVALID EMAIL FORMAT DETECTED";
      else if (err.code === 'auth/email-already-in-use') errMsg = "IDENTITY ALREADY EXISTS IN SYSTEM";
      else if (err.code === 'auth/weak-password') errMsg = "WEAK PRIVATE KEY — USE AT LEAST 6 CHARACTERS";
      else if (err.message === "Credentials required") errMsg = "ALL CREDENTIALS REQUIRED";
      else if (err.message === "Display Name required for signup") errMsg = "OPERATIVE ALIAS IS REQUIRED";
      else errMsg = `ERROR: ${err.code || err.message}`; // Show exact Firebase error

      setError(errMsg);
      gsap.to('.lp-card', { x: 10, duration: 0.07, repeat: 8, yoyo: true });
      setTimeout(() => setError(''), 3500);
    }
  }, [identity, key, isSignup, displayName]);

  const handleGoogleBypass = useCallback(async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      if (result.user.metadata.creationTime === result.user.metadata.lastSignInTime) {
        await setDoc(doc(db, 'u-jarvis-status', 'analytics'), { totalUsers: increment(1) }, { merge: true });
      }
      setError('');
      const tl = gsap.timeline();
      tl.to('.lp-card', { duration: 0.6, scale: 0.88, opacity: 0, ease: 'power4.in', onComplete: () => setHacking(true) });
      setSpeed(5);
    } catch (err) {
      console.error(err);
      setError("GOOGLE BYPASS FAILED");
      gsap.to('.lp-card', { x: 10, duration: 0.07, repeat: 8, yoyo: true });
      setTimeout(() => setError(''), 2800);
    }
  }, []);

  const handleSequenceComplete = useCallback(() => {
    gsap.to('.lp-overlay', {
      duration: 1.2, height: '100%', ease: 'power4.inOut', onComplete: onUnlock,
    });
  }, [onUnlock]);

  /* ── DOMINATION theme colours ── */
  const VIOLET  = '#FFD700';   // was violet, now gold for domination
  const MAGENTA = '#FF8C00';   // amber-orange accent
  const GOLD    = '#FFD700';
  const PURPLE  = '#FFC107';   // warm amber instead of purple
  const theme   = error ? '#FF3131' : PURPLE;

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: '#050300',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden', fontFamily: "'Fira Code', monospace",
    }}>
      {/* ── Matrix canvas background ── */}
      <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }} />

      {/* ── Scanlines effect ── */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.18) 3px, rgba(0,0,0,0.18) 6px)',
      }} />

      {/* ── Glitch scanline flicker ── */}
      <div className="glitch-scanline" style={{
        position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none',
        background: 'linear-gradient(transparent 0%, rgba(255,200,0,0.022) 50%, transparent 100%)',
        backgroundSize: '100% 8px',
      }} />

      {/* ── Gold ambient glow (center) ── */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none',
        background: 'radial-gradient(ellipse 65% 65% at 50% 50%, rgba(180,120,0,0.10) 0%, transparent 70%)',
      }} />

      {/* ── Radial vignette ── */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none',
        background: 'radial-gradient(ellipse 70% 70% at 50% 50%, transparent 25%, rgba(0,0,0,0.88) 100%)',
      }} />

      {/* ── Hacker Ring Loaders (behind card) ── */}
      {!hacking && <HackerRings theme={theme} error={error} />}

      {/* ──────────────────────── LOGIN CARD ──────────────────────────────── */}
      {!hacking && (
        <div className="lp-card" style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 400, padding: '0 16px' }}>

          {/* Outer glow border wrapper */}
          <div style={{
            padding: 1.5, borderRadius: 14,
            background: error
              ? 'linear-gradient(145deg,#FF006E,#FF006E55,transparent,#FF006E)'
              : 'linear-gradient(145deg,#FFD700,#FFC10744,transparent,#FF8C0088)',
            boxShadow: error
              ? '0 0 45px rgba(255,0,110,0.40), 0 0 90px rgba(255,0,110,0.12)'
              : `0 0 50px rgba(255,215,0,0.22), 0 0 100px rgba(255,160,0,0.10), 0 0 160px rgba(255,120,0,0.05)`,
            animation: 'borderSpin 6s linear infinite, cardGlitch 9s infinite',
          }}>
            {/* Glass inner surface */}
            <div style={{
              background: 'rgba(3,0,12,0.92)',
              backdropFilter: 'blur(22px)',
              borderRadius: 13,
              padding: '44px 38px 38px',
              position: 'relative',
            }}>

              {/* Noise texture overlay */}
              <div style={{
                position: 'absolute', inset: 0, borderRadius: 13, pointerEvents: 'none', zIndex: 0,
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E")`,
                backgroundSize: '180px 180px',
              }} />

              {/* Inner content above noise */}
              <div style={{ position: 'relative', zIndex: 1 }}>

                {/* Corner brackets */}
                {[['top','left'],['top','right'],['bottom','left'],['bottom','right']].map(([v,h]) => (
                  <div key={v+h} style={{
                    position: 'absolute', [v]: 14, [h]: 14, width: 18, height: 18,
                    borderTop:    v==='top'    ? `1.5px solid ${theme}` : 'none',
                    borderBottom: v==='bottom' ? `1.5px solid ${theme}` : 'none',
                    borderLeft:   h==='left'   ? `1.5px solid ${theme}` : 'none',
                    borderRight:  h==='right'  ? `1.5px solid ${theme}` : 'none',
                    opacity: 0.9,
                    filter: `drop-shadow(0 0 4px ${theme})`,
                  }} />
                ))}

                {/* Status LED row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 30 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: GOLD, boxShadow: `0 0 10px ${GOLD}`, animation: 'blink 1.4s ease-in-out infinite' }} />
                  <span style={{ fontSize: 9, color: 'rgba(255,180,0,0.55)', letterSpacing: '0.35em' }}>
                    SYSTEM_ONLINE // {new Date().toISOString().slice(0,10)}
                  </span>
                </div>

                {/* Logo / Title */}
                <div style={{ textAlign: 'center', marginBottom: 36 }}>
                  <div style={{
                    fontSize: 28, fontWeight: 900, letterSpacing: '0.25em',
                    color: GOLD,
                    textShadow: `0 0 20px ${GOLD}, 0 0 50px rgba(255,215,0,0.25), 0 0 2px #fff`,
                    animation: 'glitch 7s infinite',
                  }}>
                    U-JARVIS
                  </div>
                  {/* DOMINATION subtitle strip */}
                  <div style={{
                    marginTop: 8,
                    background: `linear-gradient(90deg, transparent, rgba(255,215,0,0.12), rgba(255,140,0,0.08), transparent)`,
                    padding: '4px 0',
                  }}>
                    <span style={{
                      fontSize: 8, letterSpacing: '0.7em', fontWeight: 700,
                      background: `linear-gradient(90deg, ${GOLD}, ${MAGENTA})`,
                      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}>
                      {error ? '[ ACCESS DENIED ]' : 'DOMINATION'}
                    </span>
                  </div>
                  <div style={{ color: 'rgba(255,180,0,0.38)', fontSize: 9, letterSpacing: '0.3em', marginTop: 4 }}>
                    {error ? '' : '[ FINANCIAL COMMAND CENTER v1.0 ]'}
                  </div>
                </div>

                {/* Display Name input (only for signup) */}
                {isSignup && (
                  <InputField
                    id="displayName"
                    type="text"
                    placeholder="OPERATIVE_ALIAS (NAME)"
                    value={displayName}
                    onChange={setDisplayName}
                    onEnter={handleSystemBypass}
                    focused={focusId === 'displayName'}
                    onFocus={() => setFocusId('displayName')}
                    onBlur={() => setFocusId(null)}
                    theme={theme}
                    error={error}
                  />
                )}

                {/* Identity input */}
                <InputField
                  id="identity"
                  type="text"
                  placeholder="SECURE_EMAIL_ID"
                  value={identity}
                  onChange={setIdentity}
                  onEnter={handleSystemBypass}
                  focused={focusId === 'identity'}
                  onFocus={() => setFocusId('identity')}
                  onBlur={() => setFocusId(null)}
                  theme={theme}
                  error={error}
                />

                {/* Private key input */}
                <InputField
                  id="key"
                  type="password"
                  placeholder="PRIVATE_KEY"
                  value={key}
                  onChange={setKey}
                  onEnter={handleSystemBypass}
                  focused={focusId === 'key'}
                  onFocus={() => setFocusId('key')}
                  onBlur={() => setFocusId(null)}
                  theme={theme}
                  error={error}
                  style={{ marginBottom: 36 }}
                />

                  <div style={{display: 'flex', gap: 10, marginTop: 8}}>
                    <button
                      id="bypass-btn"
                      onClick={handleSystemBypass}
                      style={{
                        flex: 1, background: 'transparent',
                        border: `1px solid ${theme}`,
                        color: theme, padding: '14px 0',
                        fontSize: 10, letterSpacing: '0.15em', fontWeight: 900,
                        cursor: 'pointer', transition: 'all 0.25s',
                        fontFamily: "'Fira Code', monospace", borderRadius: 4,
                        textShadow: `0 0 8px ${theme}`,
                        boxShadow: `0 0 0px ${theme}, inset 0 0 0px transparent`,
                      }}
                      onMouseOver={e => {
                        if (!error) {
                          e.currentTarget.style.background = `linear-gradient(135deg, ${VIOLET}dd, ${MAGENTA}99)`;
                          e.currentTarget.style.color = '#fff';
                          e.currentTarget.style.boxShadow = `0 0 30px ${VIOLET}, 0 0 60px rgba(255,0,110,0.2)`;
                          e.currentTarget.style.textShadow = '0 0 12px #fff';
                        }
                      }}
                      onMouseOut={e => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = theme;
                        e.currentTarget.style.boxShadow = '0 0 0px transparent';
                        e.currentTarget.style.textShadow = `0 0 8px ${theme}`;
                      }}
                    >
                      {isSignup ? 'CREATE IDENTITY' : 'INITIALIZE'}
                    </button>

                    <button
                      id="google-bypass-btn"
                      onClick={handleGoogleBypass}
                      style={{
                        flex: 1, background: 'transparent',
                        border: `1px solid #4285F4`,
                        color: '#4285F4', padding: '14px 0',
                        fontSize: 10, letterSpacing: '0.15em', fontWeight: 900,
                        cursor: 'pointer', transition: 'all 0.25s',
                        fontFamily: "'Fira Code', monospace", borderRadius: 4,
                        textShadow: `0 0 8px #4285F4`,
                        boxShadow: `0 0 0px #4285F4, inset 0 0 0px transparent`,
                      }}
                      onMouseOver={e => {
                        if (!error) {
                          e.currentTarget.style.background = `linear-gradient(135deg, #4285F4dd, #34A85399)`;
                          e.currentTarget.style.color = '#fff';
                          e.currentTarget.style.boxShadow = `0 0 30px #4285F4, 0 0 60px rgba(66,133,244,0.2)`;
                          e.currentTarget.style.textShadow = '0 0 12px #fff';
                        }
                      }}
                      onMouseOut={e => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#4285F4';
                        e.currentTarget.style.boxShadow = '0 0 0px transparent';
                        e.currentTarget.style.textShadow = `0 0 8px #4285F4`;
                      }}
                    >
                      GOOGLE BYPASS
                    </button>
                  </div>

                  <div 
                    onClick={() => setIsSignup(!isSignup)}
                    style={{
                      marginTop: 20, textAlign: 'center', fontSize: 9, 
                      color: 'rgba(255,180,0,0.65)', letterSpacing: '0.25em', 
                      cursor: 'pointer', transition: 'color 0.2s',
                      fontFamily: "'Fira Code', monospace",
                    }}
                    onMouseOver={e => e.currentTarget.style.color = '#fff'}
                    onMouseOut={e => e.currentTarget.style.color = 'rgba(255,180,0,0.65)'}
                  >
                    {isSignup ? '< BACK TO SECURE LOGIN' : '> NEW RECRUIT? JOIN TO HUB'}
                  </div>

                {/* Error message */}
                {error && (
                  <div style={{
                    marginTop: 16, textAlign: 'center', fontSize: 9,
                    color: MAGENTA, letterSpacing: '0.3em',
                    animation: 'fadeIn 0.2s ease',
                    textShadow: `0 0 8px ${MAGENTA}`,
                  }}>
                    ✗ {error}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Hack terminal overlay ── */}
      {hacking && <HackTerminal onComplete={handleSequenceComplete} />}

      {/* ── Final transition overlay ── */}
      <div className="lp-overlay" style={{
        position: 'absolute', top: 0, left: 0, width: '100%', height: 0,
        background: 'linear-gradient(180deg, #000 0%, #06000f 100%)',
        zIndex: 300, overflow: 'hidden',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderBottom: `2px solid ${MAGENTA}`,
      }}>
        <h1 style={{
          color: GOLD, fontSize: 24, fontWeight: 900,
          letterSpacing: '1.2em', fontFamily: "'Fira Code', monospace",
          textShadow: `0 0 40px ${GOLD}, 0 0 80px rgba(255,215,0,0.2)`,
        }}>
          ACCESS_GRANTED
        </h1>
      </div>

      {/* ── Footer status bar ── */}
      <div style={{
        position: 'absolute', bottom: 24, left: 0, right: 0, zIndex: 10,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '0 36px', fontSize: 9,
        fontFamily: "'Fira Code', monospace", color: 'rgba(191,0,255,0.35)',
        letterSpacing: '0.25em',
      }}>
        <span>OWNER: <span style={{ color: GOLD }}>MR DEVELOPER</span></span>
        <span style={{ color: 'rgba(139,0,255,0.2)' }}>
          {Array.from({length: 12}, () => Math.floor(Math.random() * 2)).join('')}
        </span>
        <span>STATUS: <span style={{ color: MAGENTA }}>DOMINATION ✓</span></span>
      </div>

      {/* ── Global keyframes ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fira+Code:wght@400;700;900&display=swap');

        /* ── GOLD GLITCH — RGB channel split ── */
        @keyframes glitch {
          0%,88%,100% { clip-path:none; transform:none; filter:none }
          89% { clip-path:polygon(0 8%,100% 8%,100% 28%,0 28%); transform:translateX(-5px); filter:drop-shadow(3px 0 0 rgba(255,80,0,0.6)) drop-shadow(-3px 0 0 rgba(0,200,255,0.4)) }
          91% { clip-path:polygon(0 60%,100% 60%,100% 75%,0 75%); transform:translateX(5px); filter:drop-shadow(-3px 0 0 rgba(255,0,120,0.5)) drop-shadow(3px 0 0 rgba(255,215,0,0.5)) }
          93% { clip-path:polygon(0 3%,100% 3%,100% 18%,0 18%);  transform:translateX(-2px) skewX(-1deg); filter:none }
          95% { clip-path:none; transform:translateX(3px); filter:drop-shadow(2px 0 0 rgba(255,215,0,0.8)) }
          97% { clip-path:polygon(0 40%,100% 40%,100% 55%,0 55%); transform:skewX(2deg); filter:none }
        }

        /* ── Card-level glitch shimmer ── */
        @keyframes cardGlitch {
          0%,94%,100% { transform:none; filter:none }
          95% { transform:translateX(-3px) skewX(-0.5deg); filter:drop-shadow(2px 0 0 rgba(255,150,0,0.3)) }
          97% { transform:translateX(3px); filter:drop-shadow(-2px 0 0 rgba(0,180,255,0.2)) }
        }

        /* ── Glitch scanline sweep ── */
        @keyframes scanSweep {
          0%   { transform:translateY(-100%) }
          100% { transform:translateY(100vh) }
        }
        .glitch-scanline {
          animation: scanSweep 4s linear infinite;
        }

        /* ── Ring animations ── */
        @keyframes ringRotateCW  { from{transform:rotate(0deg)}   to{transform:rotate(360deg)} }
        @keyframes ringRotateCCW { from{transform:rotate(0deg)}   to{transform:rotate(-360deg)} }
        @keyframes ringPulse     { 0%,100%{opacity:0.55} 50%{opacity:1} }
        @keyframes ringDash      { to{stroke-dashoffset:-300} }

        @keyframes blink {
          0%,100% { opacity:1 } 50% { opacity:0.15 }
        }
        @keyframes fadeIn {
          from { opacity:0 } to { opacity:1 }
        }
        @keyframes borderSpin {
          0%   { filter: hue-rotate(0deg) brightness(1) }
          50%  { filter: hue-rotate(15deg) brightness(1.18) }
          100% { filter: hue-rotate(0deg) brightness(1) }
        }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 0; }
      `}</style>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   INPUT FIELD — Reusable styled terminal input
───────────────────────────────────────────────────────────────────────────── */

const InputField = ({ id, type, placeholder, value, onChange, onEnter, focused, onFocus, onBlur, theme, error, style: extraStyle }) => (
  <div style={{ marginBottom: 22, position: 'relative', ...extraStyle }}>
    <div style={{
      fontSize: 8, letterSpacing: '0.4em',
      color: focused ? theme : 'rgba(255,180,0,0.32)',
      marginBottom: 7, transition: 'color 0.2s',
    }}>
      {`> ${placeholder}`}
    </div>
    <input
      id={id}
      type={type}
      placeholder=""
      autoComplete="off"
      value={value}
      onChange={e => onChange(e.target.value)}
      onKeyDown={e => e.key === 'Enter' && onEnter()}
      onFocus={onFocus}
      onBlur={onBlur}
      style={{
        width: '100%',
        background: focused ? 'rgba(255,200,0,0.04)' : 'transparent',
        border: 'none',
        borderBottom: `1.5px solid ${focused ? theme : (error ? 'rgba(255,49,49,0.4)' : 'rgba(255,180,0,0.18)')}`,
        padding: '10px 6px',
        color: '#FFE082',
        fontSize: 13,
        letterSpacing: '0.2em',
        outline: 'none',
        fontFamily: "'Fira Code', monospace",
        caretColor: theme,
        transition: 'all 0.2s',
      }}
    />
    {focused && (
      <div style={{
        position: 'absolute', bottom: 0, left: 0, height: '1.5px', width: '100%',
        background: `linear-gradient(90deg, ${theme}, rgba(255,160,0,0.5), transparent)`,
        animation: 'inputGlow 1.5s ease-in-out infinite',
        opacity: 0.7,
      }} />
    )}
    <style>{`
      @keyframes inputGlow {
        0%,100% { opacity:0.4 } 50% { opacity:1 }
      }
      input:-webkit-autofill,
      input:-webkit-autofill:hover,
      input:-webkit-autofill:focus,
      input:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 1000px #050300 inset !important;
        -webkit-text-fill-color: #FFD700 !important;
        caret-color: #FFC107 !important;
        transition: background-color 5000s ease-in-out 0s;
      }
    `}</style>
  </div>
);

export default LandingPage;